/**
 * Colour utilities.
 */

/**
 * Returns a readable foreground colour (near-black or white) for text/icons
 * placed on top of the given background colour, chosen by WCAG relative
 * luminance. Falls back to white for unparseable input.
 *
 * Used for the send button, whose background is the brand primary. The brand
 * object exposes no foreground token, so we derive a contrast-safe colour here
 * rather than hardcoding "white" (which would fail on a light brand primary).
 *
 * @param backgroundHex - Background colour as #RGB or #RRGGBB.
 * @returns "#1f2937" (dark) on light backgrounds, "#ffffff" on dark ones.
 */
export function getReadableTextColor(backgroundHex: string): string {
  const hex = backgroundHex.replace("#", "").trim();
  const full =
    hex.length === 3
      ? hex
          .split("")
          .map((c) => c + c)
          .join("")
      : hex;

  if (full.length !== 6 || /[^0-9a-fA-F]/.test(full)) return "#ffffff";

  const toLinear = (channel: number) => {
    const c = channel / 255;

    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };

  const r = toLinear(parseInt(full.slice(0, 2), 16));
  const g = toLinear(parseInt(full.slice(2, 4), 16));
  const b = toLinear(parseInt(full.slice(4, 6), 16));
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  // Threshold chosen so mid-dark brand primaries (e.g. the WBAI orange) keep
  // white text, while light primaries switch to dark text for contrast.
  return luminance > 0.45 ? "#1f2937" : "#ffffff";
}
